import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Package,
  Building2,
  DollarSign,
  Clock,
  Users,
  Plus,
  CheckCircle
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useCreateBookingOption } from "@/hooks/useBookings";
import type { CreateBookingOptionRequest } from "@/types/activities";
import type { ServiceOptionResponse } from "@/types/services";

interface AddMarketplaceOptionModalProps {
  open: boolean;
  onClose: () => void;
  bookingId: string;
  serviceOptionId: string;
  serviceOptionData?: ServiceOptionResponse;
  onSuccess?: () => void;
}

interface FormData {
  contactNotes: string;
}

export default function AddMarketplaceOptionModal({
  open,
  onClose,
  bookingId,
  serviceOptionId,
  serviceOptionData,
  onSuccess
}: AddMarketplaceOptionModalProps) {
  const { toast } = useToast();
  const createBookingOption = useCreateBookingOption();

  const serviceOption = serviceOptionData;
  const optionLoading = false;

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors }
  } = useForm<FormData>({
    defaultValues: {
      contactNotes: ""
    }
  });

  // Set default contact notes when modal opens
  useEffect(() => {
    if (open && serviceOption) {
      setValue("contactNotes",
        `Exploring ${serviceOption.name} as alternative option from marketplace exploration. ` +
        `Different service option that may better fit our requirements.`
      );
    }
  }, [open, serviceOption, setValue]);

  const onSubmit = async (data: FormData) => {
    try {
      await createBookingOption.mutateAsync({
        bookingId,
        data: {
          serviceOptionId,
          contactNotes: data.contactNotes
        }
      });

      toast({
        title: "Option added",
        description: "Marketplace option has been added to your booking and vendor will be contacted.",
        duration: 5000
      });

      onSuccess?.();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to add marketplace option. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const formatPrice = (price?: number, currency: string = 'AED') => {
    if (!price) return "Custom Pricing";
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency
    }).format(price);
  };

  const getDurationText = (duration?: number) => {
    if (!duration) return "Custom Duration";
    if (duration < 60) return `${duration} minutes`;
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours} hours`;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5" />
            Add Marketplace Option
          </DialogTitle>
          <DialogDescription>
            Adding a new booking option from marketplace exploration
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Loading State */}
          {optionLoading && (
            <div className="space-y-4">
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          )}

          {/* Service Option Details */}
          {serviceOption && (
            <div className="space-y-4">
              <div className="p-4 bg-muted/30 rounded-lg">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Package className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium">Selected Option: {serviceOption.name || serviceOption.service?.name || 'Service Option'}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <Building2 className="w-4 h-4 text-muted-foreground" />
                      <span className="text-muted-foreground text-sm">
                        {serviceOption.service?.vendorName || serviceOption.vendorName || 'Unknown Vendor'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    <DollarSign className="w-3 h-3 text-muted-foreground" />
                    <span>{formatPrice(serviceOption.basePrice)}</span>
                  </div>
                  {serviceOption.durationMinutes && (
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3 text-muted-foreground" />
                      <span>{getDurationText(serviceOption.durationMinutes)}</span>
                    </div>
                  )}
                  {serviceOption.maxCapacity && (
                    <div className="flex items-center gap-1">
                      <Users className="w-3 h-3 text-muted-foreground" />
                      <span>{serviceOption.maxCapacity} people</span>
                    </div>
                  )}
                </div>

                {serviceOption.description && (
                  <div className="mt-3">
                    <p className="text-sm text-muted-foreground">{serviceOption.description}</p>
                  </div>
                )}

                {serviceOption.includedItems && serviceOption.includedItems.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs text-muted-foreground mb-2">Included:</p>
                    <div className="flex flex-wrap gap-1">
                      {serviceOption.includedItems.map((item, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {item}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Contact Notes */}
              <div className="space-y-2">
                <Label htmlFor="contactNotes">Contact Notes</Label>
                <Textarea
                  id="contactNotes"
                  {...register("contactNotes", {
                    required: "Contact notes are required"
                  })}
                  placeholder="Enter notes for vendor contact explaining why this option is being explored..."
                  rows={4}
                  className="resize-none"
                />
                {errors.contactNotes && (
                  <p className="text-sm text-destructive">{errors.contactNotes.message}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  These notes will be shared with the vendor when requesting availability and pricing.
                </p>
              </div>

              {/* Action Preview */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-4 w-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-800">Next Action</span>
                </div>
                <p className="text-sm text-blue-700">
                  This marketplace option will be added to your booking with "Exploring" status.
                  The vendor will be contacted for availability and pricing confirmation.
                </p>
              </div>
            </div>
          )}

          {/* No option found */}
          {!optionLoading && !serviceOption && (
            <div className="p-8 text-center">
              <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-medium text-foreground mb-2">Service Option Not Found</h3>
              <p className="text-sm text-muted-foreground">
                The selected service option could not be loaded. Please try again.
              </p>
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createBookingOption.isPending || !serviceOption}
              className="flex items-center gap-2"
            >
              {createBookingOption.isPending ? (
                "Adding Option..."
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  Add Option & Contact Vendor
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}